/* @flow */
import * as React from 'react';
import cx from 'classnames';
import wait from 'utils/wait';
import Text from 'components/Text';
import openPopup from 'utils/openPopup';
import Image from 'components/Image';
import Glyph from 'components/Glyph';
import Button from 'components/Button';
import EventTracking from 'lib/EventTracking';
import type { GlyphName } from '@remind/r101-glyphs';
import { MODAL_FILE_CONTEXT_KEY } from 'reducers/fileUploads';
import LoadingIndicator from 'components/LoadingIndicator';
import ReduxUploadButton from 'components/ReduxUploadButton';
import { settingsForProvider } from '../../components/lib/AccountLinkingWindowSettings';
import { diffSourcesByLinked, showAccountLinkingToastsForDiff } from 'utils/accountLinkingToast';
// eslint-disable-next-line no-unused-vars
import type { Element } from 'react';

import './styles/AccountLinkingComposerSidebarServices.scss';

type ContentSourceLinkedAccount = {
  authUrl: string,
  providerKey: string,
  isLinked: boolean,
};

export type ContentSource = {
  title: string,
  providerKey: string,
  iconURL: string,
  linkedAccountInfo?: ContentSourceLinkedAccount,
};

type Props = {
  error?: Error,
  loading: boolean,
  sources?: ?Array<ContentSource>,
  onViewContent: (contentSource: *) => void,
  reloadContentSources: void => void,
};

type State = {
  linkingSource: *,
};

class AccountLinkingComposerSidebarServices extends React.Component<Props, State> {
  state = {
    linkingSource: null,
  };

  componentWillUpdate(nextProps: Props) {
    ((oldSources, newSources) => {
      if (!oldSources || !newSources) {
        return;
      }

      const diff = diffSourcesByLinked(oldSources, newSources);
      showAccountLinkingToastsForDiff(diff);

      if (this.state.linkingSource && diff.newlyLinked[this.state.linkingSource.providerKey]) {
        this.props.onViewContent(this.state.linkingSource);
      }
    })(this.props.sources, nextProps.sources);
  }

  linkAccount(service: *) {
    // if not linkable account or already linked, show content
    if (!service.linkedAccountInfo || service.linkedAccountInfo.isLinked) {
      this.props.onViewContent(service);
      EventTracking.trackKey('account_linking.composer.content_provider.click', {
        provider: service.providerKey,
        composer_version: 'web_modal',
      });
    } else {
      // otherwise link the account
      const popup = openPopup(
        `${service.linkedAccountInfo.authUrl}`,
        `Link with ${service.title}`,
        settingsForProvider(service.linkedAccountInfo.providerKey)
      );

      wait(() => popup.closed).then(this.props.reloadContentSources, e => {
        console.debug(e, e && e.stack);
        console.error('Error while waiting for account linking popup to close');
        // Reload content sources anyway - hope for the best
        this.props.reloadContentSources();
      });

      EventTracking.trackKey('account_linking.composer.connect.click', {
        provider: service.providerKey,
        composer_version: 'web_modal',
      });

      this.setState({
        linkingSource: service,
      });
    }
  }

  renderUploadButton(text: string, color: string, glyph: GlyphName, eventKey: string) {
    return (
      <ReduxUploadButton
        className="AccountLinkingComposerSidebarServices-button"
        fileContextKey={MODAL_FILE_CONTEXT_KEY}
        eventKey={eventKey}
        eventProperties={{
          composer_version: 'web_modal',
        }}
        level="primary"
        weight="normal"
        color={color}>
        <div
          className={cx(
            'AccountLinkingComposerSidebarServices-button-icon',
            'AccountLinkingComposerSidebarServices-button-icon--rounded',
            `AccountLinkingComposerSidebarServices-button-icon--${color}`
          )}>
          <Glyph
            className="AccountLinkingComposerSidebarServices-button-icon-glyph"
            name={glyph}
            size="large"
            solo
          />
        </div>
        <span className="AccountLinkingComposerSidebarServices-button-text">{text}</span>
      </ReduxUploadButton>
    );
  }

  renderServiceButton = (service: *) => {
    return (
      <Button
        key={service.providerKey}
        className="AccountLinkingComposerSidebarServices-button"
        level="primary"
        weight="normal"
        data-test="service-button"
        onClick={() => this.linkAccount(service)}
        eventElementName={`AccountLinkingComposerSidebarServices-button-${service.providerKey}`}>
        <div className="AccountLinkingComposerSidebarServices-button-icon">
          {service.linkedAccountInfo && !service.linkedAccountInfo.isLinked && (
            <div className="AccountLinkingComposerSidebarServices-button-icon-plus">
              <Glyph
                className="AccountLinkingComposerSidebarServices-button-icon-plus-glyph"
                size="xxxsmall"
                name="plus"
                color="brand"
              />
            </div>
          )}
          <Image
            src={service.iconURL}
            className="AccountLinkingComposerSidebarServices-button-icon-image"
          />
        </div>
        <span className="AccountLinkingComposerSidebarServices-button-text">{service.title}</span>
      </Button>
    );
  };

  renderServices(): React.Element | Array<React.Element> | null {
    const { sources, loading } = this.props;

    if (loading) {
      return (
        <LoadingIndicator
          className="AccountLinkingComposerSidebarServices-loading"
          align="center"
        />
      );
    }

    if (sources) {
      return sources.map(this.renderServiceButton);
    } else {
      return null;
    }
  }

  render() {
    return (
      <div className="AccountLinkingComposerSidebarServices">
        <Text align="left" size="medium" color="pitch" weight="semibold">
          Add to your message
        </Text>
        <div className="AccountLinkingComposerSidebarServices-content">
          {this.renderUploadButton(
            'Upload photo or video',
            'cactus',
            'image',
            'announcements.announcement_modal.universal_composer.upload_photo.click'
          )}
          {this.renderUploadButton(
            'Upload a file',
            'caribbean',
            'paperclip',
            'announcements.announcement_modal.universal_composer.upload_file.click'
          )}
          {this.renderServices()}
        </div>
      </div>
    );
  }
}

export { AccountLinkingComposerSidebarServices };
export default AccountLinkingComposerSidebarServices;
